import { useState, useEffect } from "react";
import { Check, Zap, ChevronDown, ArrowRight, X, ArrowLeft } from "lucide-react";
import apiService from "../services/api";
import { Stepper } from "../arc/components/stepper/stepper";
import {
  PLAN_META,
  PLAN_CAPABILITIES,
  CAP,
} from "../config/planAccess";

// ─── Data ─────────────────────────────────────────────────────────────────────
// UPDATED: monthly-only pricing (no annual toggle). "price" replaces the old
// priceMonthly/priceYearly pair. Feature lists carried over from the new design.

const PLANS = [
  {
    id: "axo_core",
    name: PLAN_META.axo_core.name,
    tagline: "Find your next customer easily.",
    price: PLAN_META.axo_core.priceGbp,
    features: [
      "Company Search",
      "Business Profiles",
      "Directors & Officers",
      "Company Timeline",
      "SIC & Industry Search",
      "Saved Companies",
      "Daily New Business Feed",
    ],
  },
  {
    id: "axo_grow",
    name: PLAN_META.axo_grow.name,
    tagline: "Understand every target business.",
    price: PLAN_META.axo_grow.priceGbp,
    popular: true,
    includesLabel: "Everything in Core, plus:",
    features: [
      "Company Enrichment",
      "Contact Discovery",
      "Website Intelligence",
      "Financial Overview",
      "Social Profiles",
      "AI Company Summary",
      "CRM Synchronisation",
      "API Access",
    ],
  },
  {
    id: "axo_ai",
    name: PLAN_META.axo_ai.name,
    tagline: "Your automated AI commercial analyst.",
    price: PLAN_META.axo_ai.priceGbp,
    includesLabel: "Everything in Grow, plus:",
    features: [
      "AI Research Agent",
      "Opportunity Score",
      "AI Recommendations",
      "AI Sales Briefings",
      "Continuous Monitoring",
      "Workflow Automation",
      "Commercial Risk Intelligence",
      "Credit Rating & Limits",
      "Director Risk Assessment",
    ],
  },
  {
    id: "axo_enterprise",
    name: PLAN_META.axo_enterprise.name,
    tagline: "Custom built around your organization.",
    price: PLAN_META.axo_enterprise.priceGbp,
    isCustom: true,
    contactSales: true,
    includesLabel: "Everything in AI, plus:",
    features: [
      "Bespoke AI Agents",
      "Custom Integrations",
      "Internal Data Sources",
      "White Label Platform",
      "Dedicated Environment",
      "Enterprise SLA & Support",
    ],
  },
];

const GROUPS = [
  { label: "All", prefix: null },
  { label: "Construction", prefix: ["41", "42", "43"] },
  {
    label: "Manufacturing",
    prefix: [
      "10", "11", "12", "13", "14", "15", "16", "17", "18", "19",
      "20", "21", "22", "23", "24", "25", "26", "27", "28", "29",
      "30", "31", "32", "33",
    ],
  },
  { label: "Retail & Trade", prefix: ["45", "46", "47"] },
  { label: "Transport", prefix: ["49", "50", "51", "52", "53"] },
  { label: "Hospitality", prefix: ["55", "56"] },
  { label: "IT & Tech", prefix: ["58", "59", "60", "61", "62", "63"] },
  { label: "Finance", prefix: ["64", "65", "66"] },
  { label: "Real Estate", prefix: ["68"] },
  { label: "Professional", prefix: ["69", "70", "71", "72", "73", "74", "75"] },
  { label: "Health", prefix: ["86", "87", "88"] },
  { label: "Education", prefix: ["85"] },
];

const SALES_EMAIL = "sales@axo.io"; // TODO: replace with real sales inbox

// ─── Shared: step indicator ────────────────────────────────────────────────────

const StepIndicator = ({ current }) => (
  <div className="plan-arc">
    <Stepper
      label="Onboarding progress"
      compact
      current={current - 1}
      steps={[
        { label: "Plan" },
        { label: "Industries" },
        { label: "Businesses" },
        { label: "Services" },
        { label: "Payment" },
      ]}
    />
  </div>
);

// ─── Shared: header / footer ───────────────────────────────────────────────────

const ModalHeader = ({ icon, title, subtitle, onClose }) => (
  <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800/80 shrink-0">
    <div className="flex items-center gap-3 min-w-0">
      <div className="p-2.5 bg-blue-600/10 border border-blue-500/20 rounded-xl text-blue-400 shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <h2 className="text-lg font-bold tracking-tight text-white truncate">{title}</h2>
        <p className="text-xs text-slate-400 truncate">{subtitle}</p>
      </div>
    </div>
    <button
      onClick={onClose}
      className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition shrink-0"
    >
      <X className="w-5 h-5" />
    </button>
  </div>
);

const ModalFooter = ({ leftText, onBack, onNext, nextLabel, loading }) => (
  <div className="px-6 sm:px-8 py-4 bg-slate-950/60 border-t border-slate-800/80 flex items-center justify-between gap-3 shrink-0">
    <div className="text-xs text-slate-400 hidden sm:block">{leftText}</div>
    <div className="flex items-center gap-2 ml-auto">
      {onBack && (
        <button
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 hover:text-white transition disabled:opacity-50"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
      )}
      <button
        onClick={onNext}
        disabled={loading}
        className="flex items-center gap-2 px-5 py-2.5 bg-white text-slate-950 hover:bg-slate-200 rounded-xl text-xs font-bold transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processing…
          </>
        ) : (
          <>
            {nextLabel}
            <ArrowRight className="w-3.5 h-3.5" />
          </>
        )}
      </button>
    </div>
  </div>
);

// ─── Step 1: Plan ───────────────────────────────────────────────────────────────
// Rebuilt using the new dark card design: expandable feature lists, popular
// ribbon, per-card select buttons. Annual billing toggle removed, monthly only.

const StepPlan = ({ selectedPlanId, onSelect, onNext, onClose }) => {
  const [expandedCards, setExpandedCards] = useState({});
  const selectedPlan = PLANS.find((p) => p.id === selectedPlanId);

  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <>
      <ModalHeader
        icon={<Zap className="w-5 h-5" />}
        title="Choose your plan"
        subtitle="Select the plan that fits your prospecting goals."
        onClose={onClose}
      />

      <div className="px-6 sm:px-8 pt-6 shrink-0">
        <StepIndicator current={1} />
      </div>

      <div className="p-6 sm:p-8 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map((plan) => {
          const isSelected = selectedPlanId === plan.id;
          const isExpanded = !!expandedCards[plan.id];
          const visibleFeatures = isExpanded ? plan.features : plan.features.slice(0, 4);
          const hiddenCount = plan.features.length - 4;

          return (
            <div
              key={plan.id}
              onClick={() => onSelect(plan.id)}
              className={`relative rounded-2xl p-5 flex flex-col justify-between cursor-pointer transition-all duration-200 border ${
                isSelected
                  ? "bg-slate-800/90 border-blue-500 ring-2 ring-blue-500/20 shadow-xl"
                  : "bg-slate-950/40 border-slate-800/80 hover:border-slate-700 hover:bg-slate-800/30"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-0.5 rounded-full shadow-md">
                  Most Popular
                </span>
              )}

              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-base text-white">{plan.name}</h3>
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center transition ${
                      isSelected ? "bg-blue-600 border-blue-600 text-white" : "border-slate-700 bg-transparent"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                  </div>
                </div>

                <p className="text-xs text-slate-400 min-h-[32px] leading-relaxed mb-4">
                  {plan.tagline}
                </p>

                <div className="mb-5 pb-4 border-b border-slate-800">
                  <div className="flex items-baseline gap-1">
                    {plan.isCustom && <span className="text-xs text-slate-400 font-medium">From</span>}
                    <span className="text-3xl font-extrabold text-white tracking-tight">£{plan.price}</span>
                    <span className="text-xs text-slate-400">/mo</span>
                  </div>
                </div>

                <div>
                  {plan.includesLabel && (
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2.5">
                      {plan.includesLabel}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {visibleFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                        <Check className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                        <span className="leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {hiddenCount > 0 && (
                    <button
                      onClick={(e) => toggleExpand(plan.id, e)}
                      className="mt-3 text-xs font-semibold text-blue-400 hover:text-blue-300 flex items-center gap-1 transition"
                    >
                      {isExpanded ? "Show less" : `+${hiddenCount} more features`}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              </div>

              <button
                onClick={() => onSelect(plan.id)}
                className={`w-full mt-6 py-2.5 px-4 rounded-xl text-xs font-bold transition-all ${
                  isSelected
                    ? "bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/25"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
                }`}
              >
                {isSelected ? "Selected Plan" : plan.isCustom ? "Select & Contact Sales" : "Select Plan"}
              </button>
            </div>
          );
        })}
      </div>

      <ModalFooter
        leftText={
          <>
            Selected: <span className="font-bold text-white">{selectedPlan?.name}</span> -{" "}
            <span className="text-slate-200">
              {selectedPlan?.isCustom ? "From " : ""}£{selectedPlan?.price}/month
            </span>
          </>
        }
        onBack={null}
        onNext={onNext}
        nextLabel="Next: Target Industries"
      />
    </>
  );
};

// ─── Step 2: Industries ─────────────────────────────────────────────────────────

const SicTag = ({ code, label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-slate-800 border border-slate-700 text-slate-200">
    <span className="font-mono text-slate-400">{code}</span>
    <span className="max-w-[110px] truncate">{label}</span>
    <button onClick={() => onRemove(code)} className="ml-0.5 text-slate-500 hover:text-white transition">
      <X className="w-3 h-3" />
    </button>
  </span>
);

const SicRow = ({ code, label, isSelected, onToggle }) => (
  <button
    onClick={() => onToggle(code, label)}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition ${
      isSelected ? "bg-slate-800 border border-slate-700" : "border border-transparent hover:bg-slate-800/50"
    }`}
  >
    <div className="flex items-center gap-2 min-w-0">
      <span className={`font-mono text-xs shrink-0 ${isSelected ? "text-slate-300" : "text-slate-500"}`}>
        {code}
      </span>
      <span className={`text-xs truncate ${isSelected ? "text-white font-semibold" : "text-slate-300"}`}>
        {label}
      </span>
    </div>
    {isSelected && <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 ml-2" />}
  </button>
);

const StepIndustries = ({
  selected,
  onToggleCode,
  onRemove,
  onClearAll,
  onBack,
  onNext,
  onClose,
  sicCodes,
  sicLoading,
}) => {
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);
  const selectedCount = Object.keys(selected).length;

  const filteredCodes = sicCodes.filter((c) => {
    const matchesSearch =
      !search.trim() ||
      c.description.toLowerCase().includes(search.toLowerCase()) ||
      c.code.includes(search.trim());
    const matchesGroup =
      !activeGroup || activeGroup.some((prefix) => c.code.startsWith(prefix));
    return matchesSearch && matchesGroup;
  });

  return (
    <>
      <ModalHeader
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        title="Target Industries"
        subtitle="Only show companies in these sectors."
        onClose={onClose}
      />

      <div className="px-6 sm:px-8 pt-6 shrink-0">
        <StepIndicator current={2} />
      </div>

      {selectedCount > 0 && (
        <div className="px-6 sm:px-8 py-3 shrink-0 border-b border-slate-800/80 bg-slate-950/30">
          <div className="flex items-start gap-2">
            <span className="text-xs font-semibold mt-1 shrink-0 text-slate-400">
              {selectedCount} selected
            </span>
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              {Object.entries(selected).map(([code, label]) => (
                <SicTag key={code} code={code} label={label} onRemove={onRemove} />
              ))}
            </div>
            <button
              onClick={onClearAll}
              className="text-xs shrink-0 mt-1 text-slate-500 hover:text-red-400 transition"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0 p-6 sm:p-8 flex flex-col gap-4">
        <div className="relative">
          <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search industries or SIC codes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 text-sm rounded-lg outline-none transition bg-slate-950 border border-slate-800 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap">
          {GROUPS.map((g) => (
            <button
              key={g.label}
              onClick={() => setActiveGroup(g.prefix)}
              className={`text-xs px-2.5 py-1 rounded-full border transition font-medium ${
                activeGroup === g.prefix
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        {sicLoading ? (
          <div className="flex items-center justify-center py-14 gap-2 text-slate-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm">Loading industries…</span>
          </div>
        ) : filteredCodes.length === 0 ? (
          <div className="text-center py-14 text-sm text-slate-500">
            {sicCodes.length === 0
              ? "No industry data available."
              : "No industries match your search."}
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {filteredCodes.map((c) => (
              <SicRow
                key={c.code}
                code={c.code}
                label={c.description}
                isSelected={!!selected[c.code]}
                onToggle={onToggleCode}
              />
            ))}
          </div>
        )}
      </div>

      <ModalFooter
        leftText={
          selectedCount === 0
            ? "Optional, skip to show all industries"
            : `${selectedCount} SIC code${selectedCount !== 1 ? "s" : ""} selected`
        }
        onBack={onBack}
        onNext={onNext}
        nextLabel={
          selectedCount > 0 ? `Continue (${selectedCount} selected)` : "Skip & Continue"
        }
      />
    </>
  );
};

// ─── Step 3: Business Types ─────────────────────────────────────────────────────

const StepBusinessType = ({
  includeSoleTraders,
  onChange,
  onBack,
  onNext,
  onClose,
  selectedPlan,
  companyAgeFilter,
  onToggleAgeFilter,
  companyAgeMode,
  onSetAgeMode,
}) => {
  const addon = includeSoleTraders ? 100 : 0;
  const total = (selectedPlan?.price || 0) + addon;

  return (
    <>
      <ModalHeader
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="Business Types"
        subtitle="Choose which business types to include."
        onClose={onClose}
      />

      <div className="px-6 sm:px-8 pt-6 shrink-0">
        <StepIndicator current={3} />
      </div>

      <div className="p-6 sm:p-8 overflow-y-auto flex-1 flex flex-col gap-4">
        {/* Limited Companies */}
        <div className="rounded-xl p-4 sm:p-5 flex items-start gap-4 border-2 border-blue-500 bg-slate-800/60">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-blue-600/10 text-blue-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-sm text-white">Limited Companies</p>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white shrink-0">
                Included
              </span>
            </div>
            <p className="text-xs mt-1 text-slate-400">
              Newly registered Ltd companies from Companies House. Verified, enriched, and sales-ready.
            </p>
          </div>
        </div>

        {/* Sole Traders */}
        <div
          onClick={() => onChange(!includeSoleTraders)}
          className={`rounded-xl cursor-pointer transition-all p-4 sm:p-5 flex items-start gap-4 border-2 ${
            includeSoleTraders ? "border-blue-500 bg-slate-800/60" : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
          }`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${includeSoleTraders ? "bg-blue-600/10 text-blue-400" : "bg-slate-800 text-slate-500"}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-sm text-white">Sole Traders</p>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-semibold text-slate-300">+£100/mo</span>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                  includeSoleTraders ? "bg-blue-600 border-blue-600" : "border-slate-700"
                }`}>
                  {includeSoleTraders && <Check className="w-2.5 h-2.5 text-white stroke-[3]" />}
                </div>
              </div>
            </div>
            <p className="text-xs mt-1 text-slate-400">
              Add sole trader registrations to your feed. Ideal for reaching self-employed professionals and tradespeople.
            </p>
          </div>
        </div>

        {/* Company Age Filter */}
        <div
          onClick={() => onToggleAgeFilter(!companyAgeFilter)}
          className={`rounded-xl cursor-pointer transition-all p-4 sm:p-5 flex items-start gap-4 border-2 ${
            companyAgeFilter ? "border-blue-500 bg-slate-800/60" : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
          }`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${companyAgeFilter ? "bg-blue-600/10 text-blue-400" : "bg-slate-800 text-slate-500"}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-sm text-white">Filter by company age</p>
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                companyAgeFilter ? "bg-blue-600 border-blue-600" : "border-slate-700"
              }`}>
                {companyAgeFilter && <Check className="w-2.5 h-2.5 text-white stroke-[3]" />}
              </div>
            </div>
            <p className="text-xs mt-1 text-slate-400">
              Show newly registered companies, established businesses, or both.
            </p>

            {companyAgeFilter && (
              <div
                className="flex mt-3 rounded-lg overflow-hidden border border-slate-800"
                onClick={(e) => e.stopPropagation()}
              >
                {[
                  { value: "new", label: "New only", sub: "0–12 months" },
                  { value: "established", label: "Established", sub: "12+ months" },
                  { value: "both", label: "Both", sub: "All ages" },
                ].map((opt, i) => (
                  <button
                    key={opt.value}
                    onClick={() => onSetAgeMode(opt.value)}
                    className={`flex-1 py-2 transition ${i > 0 ? "border-l border-slate-800" : ""} ${
                      companyAgeMode === opt.value ? "bg-blue-600/15" : ""
                    }`}
                  >
                    <p className={`text-xs font-semibold ${companyAgeMode === opt.value ? "text-white" : "text-slate-500"}`}>
                      {opt.label}
                    </p>
                    <p className="text-[10px] text-slate-500">{opt.sub}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Price summary */}
        <div className="rounded-xl p-4 mt-auto bg-slate-950/60 border border-slate-800">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{selectedPlan?.name}</span>
              <span>{selectedPlan?.isCustom ? "From " : ""}£{selectedPlan?.price}/mo</span>
            </div>
            {includeSoleTraders && (
              <div className="flex items-center justify-between text-xs text-slate-300">
                <span>Sole Traders add-on</span>
                <span>+£100/mo</span>
              </div>
            )}
            <div className="pt-1.5 mt-0.5 flex items-center justify-between border-t border-slate-800">
              <span className="text-sm font-semibold text-white">Total</span>
              <span className="text-sm font-bold text-white">
                {selectedPlan?.isCustom ? "From " : ""}£{total}/mo
              </span>
            </div>
          </div>
        </div>
      </div>

      <ModalFooter
        leftText={`Total: £${total}/mo`}
        onBack={onBack}
        onNext={onNext}
        nextLabel="Next: Your services"
      />
    </>
  );
};

// ─── Step 4: Your services ─────────────────────────────────────────────────────

let serviceRowSeq = 0;
const emptyService = () => ({
  id: `svc-${Date.now()}-${++serviceRowSeq}`,
  name: "",
  description: "",
  traits: "",
  productKey: "",
  priceGbp: "",
});

const StepService = ({
  services,
  onFieldChange,
  onAddService,
  onRemoveService,
  onBack,
  onNext,
  onClose,
}) => {
  const [error, setError] = useState("");

  const filledServices = services.filter((s) =>
    [s.name, s.description, s.traits].some((v) => String(v || "").trim()),
  );

  const handleNext = () => {
    if (filledServices.length === 0) {
      setError("Add at least one service.");
      return;
    }
    for (const svc of filledServices) {
      if (svc.name.trim().length < 2) {
        setError("Add a name for every service.");
        return;
      }
      if (svc.description.trim().length < 10) {
        setError("Add a short description for every service.");
        return;
      }
      if (
        String(svc.priceGbp ?? "").trim() !== "" &&
        !(Number(svc.priceGbp) > 0)
      ) {
        setError("Enter a valid deal value or leave it blank.");
        return;
      }
    }
    setError("");
    onNext();
  };

  const inputClass =
    "w-full rounded-lg px-3.5 py-2.5 text-sm bg-slate-950/60 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40";

  return (
    <>
      <ModalHeader
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
        title="Your services"
        subtitle="Add the services you sell so every briefing and draft fits your business."
        onClose={onClose}
      />

      <div className="px-6 sm:px-8 pt-6 shrink-0">
        <StepIndicator current={4} />
      </div>

      <div className="p-6 sm:p-8 overflow-y-auto flex-1 flex flex-col gap-4">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <svg className="w-4 h-4 shrink-0 mt-0.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {services.map((svc, index) => {
          const traitCount = svc.traits
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean).length;

          return (
            <div
              key={svc.id}
              className="rounded-xl p-4 sm:p-5 border border-slate-800 bg-slate-950/40 flex flex-col gap-4"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold text-slate-300">
                  Service {index + 1}
                </p>
                {services.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemoveService(svc.id)}
                    className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-red-300 transition"
                  >
                    <X className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Service name
                </label>
                <input
                  type="text"
                  value={svc.name}
                  maxLength={120}
                  onChange={(e) =>
                    onFieldChange(svc.id, "name", e.target.value)
                  }
                  placeholder="e.g. Commercial cleaning"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  What does it include?
                </label>
                <textarea
                  value={svc.description}
                  maxLength={2000}
                  rows={4}
                  onChange={(e) =>
                    onFieldChange(svc.id, "description", e.target.value)
                  }
                  placeholder="Who you help, what you deliver, and what makes it different."
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Traits{" "}
                  <span className="font-normal text-slate-500">optional</span>
                </label>
                <input
                  type="text"
                  value={svc.traits}
                  maxLength={600}
                  onChange={(e) =>
                    onFieldChange(svc.id, "traits", e.target.value)
                  }
                  placeholder="e.g. fixed pricing, 24-hour turnaround, local team"
                  className={inputClass}
                />
                <p className="text-[11px] mt-1.5 text-slate-500">
                  Separate traits with commas.
                  {traitCount > 0 ? ` ${traitCount} added.` : ""}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Typical deal value (£){" "}
                  <span className="font-normal text-slate-500">optional</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={svc.priceGbp}
                  onChange={(e) =>
                    onFieldChange(svc.id, "priceGbp", e.target.value)
                  }
                  placeholder="e.g. 2500"
                  className={inputClass}
                />
                <p className="text-[11px] mt-1.5 text-slate-500">
                  Used to estimate pipeline value in Phase 5. AXO maps the
                  service to a product during onboarding.
                </p>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={onAddService}
          className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-700 bg-slate-950/30 px-4 py-3 text-xs font-semibold text-slate-300 hover:border-blue-500 hover:text-white transition"
        >
          <span className="text-base leading-none">+</span> Add another service
        </button>

        <div className="rounded-xl p-4 bg-slate-950/60 border border-slate-800">
          <p className="text-xs text-slate-300 font-semibold mb-1">
            Your service memo
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            AXO saves these services and uses them to tailor the daily
            briefing, company research and outreach drafts to what you actually
            sell. You can update them later in Account settings.
          </p>
        </div>
      </div>

      <ModalFooter
        leftText="Saved to your workspace when you continue."
        onBack={onBack}
        onNext={handleNext}
        nextLabel="Next: Review & Pay"
      />
    </>
  );
};

// ─── Step 4 (locked): Phase 5 is AI / Enterprise only ─────────────────────────

const StepServiceLocked = ({
  selectedPlan,
  onBack,
  onContinue,
  onSwitchToAi,
  onClose,
}) => {
  const aiPrice = PLAN_META.axo_ai?.priceGbp;

  return (
    <>
      <ModalHeader
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        }
        title="Your services"
        subtitle="Service memo and Revenue Intelligence are available on AXO AI and Enterprise."
        onClose={onClose}
      />

      <div className="px-6 sm:px-8 pt-6 shrink-0">
        <StepIndicator current={4} />
      </div>

      <div className="p-6 sm:p-8 overflow-y-auto flex-1 flex flex-col gap-4">
        <div className="rounded-xl p-6 sm:p-8 border border-slate-800 bg-slate-950/40 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-blue-600/10 border border-blue-500/20 text-blue-400 flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-white">
            Your {selectedPlan?.name} plan doesn't include Phase 5
          </h3>
          <p className="text-sm text-slate-400 max-w-md leading-relaxed">
            The service memo, daily briefing and the rest of the Sales
            Workspace unlock on AXO AI and Enterprise. Your signup can continue
            without them, or you can switch plan now.
          </p>
          <button
            type="button"
            onClick={onSwitchToAi}
            className="mt-1 flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white transition shadow-md"
          >
            <Zap className="w-3.5 h-3.5" />
            Switch to AXO AI
            {aiPrice ? ` · £${aiPrice}/mo` : ""}
          </button>
          <p className="text-[11px] text-slate-500">
            You can also continue without it and upgrade later.
          </p>
        </div>
      </div>

      <ModalFooter
        leftText={`${selectedPlan?.name || "This plan"} doesn't include Phase 5`}
        onBack={onBack}
        onNext={onContinue}
        nextLabel="Continue without it"
      />
    </>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────────

const PlanSelected = ({ onClose, userData }) => {
  const [step, setStep] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState("axo_grow");
  const [selectedIndustries, setSelectedIndustries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [includeSoleTraders, setIncludeSoleTraders] = useState(false);
  const [sicCodes, setSicCodes] = useState([]);
  const [sicLoading, setSicLoading] = useState(false);
  const [companyAgeFilter, setCompanyAgeFilter] = useState(false);
  const [companyAgeMode, setCompanyAgeMode] = useState("new");
  const [services, setServices] = useState(() => [emptyService()]);

  const updateServiceField = (id, key, value) =>
    setServices((prev) =>
      prev.map((svc) => (svc.id === id ? { ...svc, [key]: value } : svc)),
    );
  const addService = () =>
    setServices((prev) => [...prev, emptyService()]);
  const removeService = (id) =>
    setServices((prev) =>
      prev.length > 1 ? prev.filter((svc) => svc.id !== id) : prev,
    );

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchSicCodes = async () => {
      setSicLoading(true);
      try {
        const data = await apiService.getSicCodes();
        if (data.success) setSicCodes(data.data);
      } catch (err) {
        console.error("Failed to load SIC codes", err);
      } finally {
        setSicLoading(false);
      }
    };
    fetchSicCodes();
  }, []);

  const toggleCode = (code, label) =>
    setSelectedIndustries((prev) => {
      const next = { ...prev };
      if (next[code]) delete next[code];
      else next[code] = label;
      return next;
    });
  const removeCode = (code) =>
    setSelectedIndustries((prev) => {
      const n = { ...prev };
      delete n[code];
      return n;
    });
  const clearAll = () => setSelectedIndustries({});

  const selectedPlan = PLANS.find((p) => p.id === selectedPlanId);
  const phase5Plan = (PLAN_CAPABILITIES[selectedPlanId] || []).includes(
    CAP.REVENUE_INTELLIGENCE,
  );
  const sicCount = Object.keys(selectedIndustries).length;
  const addonPrice = includeSoleTraders ? 100 : 0;
  const totalPrice = (selectedPlan?.price || 0) + addonPrice;

  const handleContinue = async () => {
    const serviceProfiles = services
      .map((svc) => ({
        name: svc.name.trim(),
        description: svc.description.trim(),
        traits: svc.traits
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
          .slice(0, 12),
        productKey: svc.productKey || null,
        priceGbp:
          String(svc.priceGbp ?? "").trim() === ""
            ? null
            : Number(svc.priceGbp),
      }))
      .filter(
        (svc) =>
          svc.name ||
          svc.description ||
          svc.traits.length ||
          svc.productKey ||
          svc.priceGbp != null,
      )
      .slice(0, 10);

    // Enterprise is "Contact sales", don't run it through self-serve Stripe checkout
    if (selectedPlan?.contactSales) {
      const subject = encodeURIComponent("AXO Enterprise, Sales enquiry");
      const serviceSummary = serviceProfiles
        .map(
          (svc, i) =>
            `Service ${i + 1}: ${svc.name}\nDetails: ${svc.description}\nTraits: ${svc.traits.join(", ")}\nRelated product: ${svc.productKey || "none"}\nTypical deal value: ${svc.priceGbp != null ? `£${svc.priceGbp}` : "not set"}`,
        )
        .join("\n\n");
      const body = encodeURIComponent(
        `Company: ${userData?.companyName || ""}\nName: ${userData?.name || ""}\nEmail: ${userData?.email || ""}\n\nServices:\n${serviceSummary || "Not specified"}\n\nHi, I'd like to talk about AXO Enterprise.`,
      );
      window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
      return;
    }

    try {
      setLoading(true);
      setError("");
      if (!userData) throw new Error("User data is missing");
      const targetSicCodes = Object.keys(selectedIndustries);
      const createResponse = await fetch(`${API_URL}/subscription/create-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: userData.companyName,
          ownerName: userData.name,
          ownerEmail: userData.email,
          password: userData.password,
          planId: selectedPlanId,
          includeSoleTraders,
          targetSicCodes,
          companyAgeFilter,
          companyAgeMode: companyAgeFilter ? companyAgeMode : null,
          markets: userData.markets || ["GB"],
          serviceProfiles,
        }),
      });
      const createResult = await createResponse.json();
      if (!createResponse.ok)
        throw new Error(createResult.message || "Failed to create account");
      sessionStorage.setItem(
        "pendingRegistration",
        JSON.stringify({
          userId: createResult.userId,
          companyId: createResult.companyId,
          email: userData.email,
          name: userData.name,
          planId: selectedPlanId,
          includeSoleTraders,
          targetSicCodes,
          markets: userData.markets || ["GB"],
          serviceProfiles,
        }),
      );
      const a = document.createElement("a");
      a.href = createResult.checkoutUrl;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reviewServices = services
    .map((svc) => ({
      name: svc.name.trim(),
      description: svc.description.trim(),
      traits: svc.traits
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      productKey: svc.productKey || null,
      priceGbp:
        String(svc.priceGbp ?? "").trim() === ""
          ? null
          : Number(svc.priceGbp),
    }))
    .filter(
      (svc) =>
        svc.name ||
        svc.description ||
        svc.traits.length ||
        svc.productKey ||
        svc.priceGbp != null,
    );
  const totalTraits = reviewServices.reduce(
    (sum, svc) => sum + svc.traits.length,
    0,
  );
  const totalServiceValue = reviewServices.reduce(
    (sum, svc) => sum + (Number(svc.priceGbp) > 0 ? Number(svc.priceGbp) : 0),
    0,
  );

  const renderStep5 = () => (
    <>
      <ModalHeader
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Review & Pay"
        subtitle={
          selectedPlan?.contactSales
            ? "Confirm your setup before contacting sales."
            : "Confirm your setup before heading to Stripe."
        }
        onClose={onClose}
      />

      <div className="px-6 sm:px-8 pt-6 shrink-0">
        <StepIndicator current={5} />
      </div>

      <div className="p-6 sm:p-8 overflow-y-auto flex-1 flex flex-col gap-4">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <svg className="w-4 h-4 shrink-0 mt-0.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {[
          {
            label: "Selected plan",
            step: 1,
            main: selectedPlan?.name,
            sub: selectedPlan?.tagline,
            right: (
              <div className="text-right shrink-0">
                <p className="text-xl sm:text-2xl font-bold text-white">
                  {selectedPlan?.isCustom ? "From " : ""}£{selectedPlan?.price}
                </p>
                <p className="text-xs text-slate-500">/month</p>
              </div>
            ),
          },
          {
            label: "Business types",
            step: 3,
            main: `Limited Companies${includeSoleTraders ? " + Sole Traders" : ""}`,
            sub: includeSoleTraders ? "+£100/mo add-on included" : null,
          },
          {
            label: "Company age",
            step: 3,
            main: companyAgeFilter
              ? { new: "New companies only", established: "Established only", both: "All ages" }[companyAgeMode]
              : "All ages",
            sub: companyAgeFilter
              ? { new: "Registered within the last 12 months", established: "Registered 12+ months ago", both: "No age filter applied" }[companyAgeMode]
              : null,
          },
          ...(phase5Plan
            ? [
                {
                  label: "Your services",
                  step: 4,
                  main:
                    reviewServices.length === 0
                      ? "Not added"
                      : reviewServices.length === 1
                        ? reviewServices[0].name
                        : `${reviewServices.length} services`,
                  sub:
                    reviewServices
                      .map((svc) => svc.name)
                      .filter(Boolean)
                      .join(", ") || null,
                  right:
                    totalTraits || totalServiceValue > 0 ? (
                      <span className="shrink-0 rounded-full border border-slate-700 bg-slate-800 px-2.5 py-0.5 text-[10px] font-semibold text-slate-300">
                        {totalServiceValue > 0
                          ? `£${totalServiceValue.toLocaleString("en-GB")}`
                          : ""}
                        {totalServiceValue > 0 && totalTraits ? " · " : ""}
                        {totalTraits
                          ? `${totalTraits} trait${totalTraits !== 1 ? "s" : ""}`
                          : ""}
                      </span>
                    ) : null,
                },
              ]
            : []),
        ].map(({ label, step: s, main, sub, right }) => (
          <div key={label} className="rounded-xl p-4 flex items-center justify-between gap-3 border border-slate-800 bg-slate-950/40">
            <div className="min-w-0">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="font-semibold text-sm text-white">{main}</p>
              {sub && <p className="text-xs mt-0.5 text-slate-400">{sub}</p>}
            </div>
            {right}
            <button
              onClick={() => setStep(s)}
              className="text-xs font-medium shrink-0 text-slate-400 hover:text-white transition"
            >
              Change
            </button>
          </div>
        ))}

        {/* Industries */}
        <div className="rounded-xl p-4 border border-slate-800 bg-slate-950/40">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs text-slate-500">Industry targeting</p>
              <p className="font-semibold text-sm text-white">
                {sicCount === 0 ? "All industries" : `${sicCount} SIC code${sicCount !== 1 ? "s" : ""} selected`}
              </p>
            </div>
            <button onClick={() => setStep(2)} className="text-xs font-medium text-slate-400 hover:text-white transition">
              {sicCount === 0 ? "Add filter" : "Edit"}
            </button>
          </div>
          {sicCount > 0 ? (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Object.entries(selectedIndustries).map(([code, label]) => (
                <span key={code} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs bg-slate-800 border border-slate-700 text-slate-300">
                  <span className="font-mono text-slate-500">{code}</span>
                  <span className="truncate max-w-[100px]">{label}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs mt-1 text-slate-500">
              You'll see companies from every industry. You can refine this later.
            </p>
          )}
        </div>

        {/* Account details */}
        <div className="rounded-xl p-4 border border-slate-800 bg-slate-950/40">
          <p className="text-xs mb-2 text-slate-500">Account details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { label: "Company", value: userData?.companyName },
              { label: "Name", value: userData?.name },
              { label: "Email", value: userData?.email },
              {
                label: "Countries",
                value: (userData?.markets || ["GB"])
                  .map(
                    (code) =>
                      ({ GB: "United Kingdom", AU: "Australia", ZA: "South Africa" })[
                        code
                      ] || code,
                  )
                  .join(", "),
              },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-xs w-14 shrink-0 text-slate-500">{label}</span>
                <span className="text-xs font-medium truncate text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Price summary */}
        <div className="rounded-xl p-4 bg-slate-950/60 border border-slate-800">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{selectedPlan?.name}</span>
              <span>{selectedPlan?.isCustom ? "From " : ""}£{selectedPlan?.price}/mo</span>
            </div>
            {includeSoleTraders && (
              <div className="flex items-center justify-between text-xs text-slate-300">
                <span>Sole Traders add-on</span>
                <span>+£100/mo</span>
              </div>
            )}
            <div className="pt-1.5 mt-0.5 flex items-center justify-between border-t border-slate-800">
              <span className="text-sm font-semibold text-white">Total</span>
              <span className="text-sm font-bold text-white">
                {selectedPlan?.isCustom ? "From " : ""}£{totalPrice}/mo
              </span>
            </div>
          </div>
        </div>
      </div>

      <ModalFooter
        leftText={
          selectedPlan?.contactSales
            ? "We'll open your email client to reach our sales team."
            : "You'll be redirected to Stripe to complete payment."
        }
        onBack={() => setStep(4)}
        onNext={handleContinue}
        nextLabel={selectedPlan?.contactSales ? "Contact Sales" : "Continue to Payment"}
        loading={loading}
      />
    </>
  );

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-6xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden text-slate-100 font-sans">
        {step === 1 && (
          <StepPlan
            selectedPlanId={selectedPlanId}
            onSelect={setSelectedPlanId}
            onNext={() => setStep(2)}
            onClose={onClose}
          />
        )}
        {step === 2 && (
          <StepIndustries
            selected={selectedIndustries}
            onToggleCode={toggleCode}
            onRemove={removeCode}
            onClearAll={clearAll}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            onClose={onClose}
            sicCodes={sicCodes}
            sicLoading={sicLoading}
          />
        )}
        {step === 3 && (
          <StepBusinessType
            companyAgeFilter={companyAgeFilter}
            onToggleAgeFilter={setCompanyAgeFilter}
            companyAgeMode={companyAgeMode}
            onSetAgeMode={setCompanyAgeMode}
            includeSoleTraders={includeSoleTraders}
            onChange={setIncludeSoleTraders}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
            onClose={onClose}
            selectedPlan={selectedPlan}
          />
        )}
        {step === 4 &&
          (phase5Plan ? (
            <StepService
              services={services}
              onFieldChange={updateServiceField}
              onAddService={addService}
              onRemoveService={removeService}
              onBack={() => setStep(3)}
              onNext={() => setStep(5)}
              onClose={onClose}
            />
          ) : (
            <StepServiceLocked
              selectedPlan={selectedPlan}
              onBack={() => setStep(3)}
              onContinue={() => setStep(5)}
              onSwitchToAi={() => setSelectedPlanId("axo_ai")}
              onClose={onClose}
            />
          ))}
        {step === 5 && renderStep5()}
      </div>
    </div>
  );
};

export default PlanSelected;